import type { IBudgetStatus } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Button, Group, Modal, NumberInput, Paper, Progress, Slider, Stack, Table, Text } from "@mantine/core"
import { TableSkeleton } from "../../../../../components/PageSkeletons"
import { formatDate, formatInteger } from "../../../../../lib/format-date"
import { formatTranscriptionBackend } from "../../../../../lib/provider-names"
import { BUDGET_ADAPTER_LABELS } from "../../constants"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

function budgetServiceLabel(adapter: string): string {
  return BUDGET_ADAPTER_LABELS[adapter] ?? formatTranscriptionBackend(adapter) ?? adapter
}

function budgetStatusColor(status: IBudgetStatus["status"]): string {
  if (status === "exceeded") return "danger"
  if (status === "warning") return "warning"
  if (status === "ok") return "success"
  return "gray"
}

export function BudgetTabView({
  hasCompany,
  budgets,
  isLoading,
  saving,
  removing,
  editState,
  onOpenEdit,
  onCloseEdit,
  onEditStateChange,
  onSaveBudget,
  onRemoveBudget,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  if (!hasCompany) {
    return (
      <Alert color="info" variant="light">
        <Trans>Budget management requires an active organization.</Trans>
      </Alert>
    )
  }

  if (isLoading) {
    return (
      <Stack gap="md">
        <TableSkeleton rows={5} cols={7} />
      </Stack>
    )
  }

  return (
    <>
      <Stack gap="md">
        <Alert variant="light" color="info">
          <Text size="sm">
            <Trans>
              Set a monthly credit limit for each service. At 100% that service stops until the next month. A warning is
              sent at the threshold you set (80% unless you change it).
            </Trans>
          </Text>
        </Alert>

        <Paper withBorder>
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Trans>Service</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Monthly limit</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Used</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Usage</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Alert at</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Resets</Trans>
                  </Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {budgets.map((b) => (
                  <Table.Tr key={b.adapter}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {budgetServiceLabel(b.adapter)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {b.monthly_limit_credits != null ? (
                        <Text size="sm">{formatInteger(i18n, b.monthly_limit_credits)} cr</Text>
                      ) : (
                        <Text size="sm" className={classes.noLimit}>
                          <Trans>No limit</Trans>
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatInteger(i18n, b.current_spend_credits)} cr</Text>
                    </Table.Td>
                    <Table.Td style={{ minWidth: 140 }}>
                      {b.percent_used != null ? (
                        <Stack gap={4}>
                          <Progress
                            value={Math.min(b.percent_used, 100)}
                            color={budgetStatusColor(b.status)}
                            size="sm"
                          />
                          <Text size="xs" c="dimmed">
                            {b.percent_used.toFixed(1)}%
                          </Text>
                        </Stack>
                      ) : (
                        <Text size="xs" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{b.alert_threshold_pct}%</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {formatDate(i18n, b.reset_date)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Button size="xs" variant="light" onClick={() => onOpenEdit(b)}>
                        <Trans>Edit</Trans>
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      </Stack>

      <Modal
        opened={editState !== null}
        onClose={onCloseEdit}
        title={i18n._(msg`Limit: ${budgetServiceLabel(editState?.adapter ?? "")}`)}
        size="sm"
      >
        {editState && (
          <Stack gap="md">
            <NumberInput
              label={i18n._(msg`Monthly limit (credits)`)}
              description={i18n._(msg`Leave blank for no limit`)}
              placeholder={i18n._(msg`e.g. 500`)}
              min={0}
              decimalScale={0}
              suffix=" cr"
              value={editState.monthly_limit_credits ?? ""}
              onChange={(v) => onEditStateChange({ ...editState, monthly_limit_credits: v === "" ? null : Number(v) })}
            />
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                <Trans>Warning threshold: {editState.alert_threshold_pct}%</Trans>
              </Text>
              <Text size="xs" c="dimmed">
                <Trans>Send a warning when usage reaches this percentage of the monthly limit.</Trans>
              </Text>
              <Slider
                min={10}
                max={100}
                step={5}
                value={editState.alert_threshold_pct}
                onChange={(v) => onEditStateChange({ ...editState, alert_threshold_pct: v })}
                marks={[
                  { value: 50, label: "50%" },
                  { value: 80, label: "80%" },
                  { value: 100, label: "100%" },
                ]}
                mb="md"
              />
            </Stack>
            <Group justify="space-between">
              <Button
                color="danger"
                variant="subtle"
                loading={removing}
                onClick={() => onRemoveBudget(editState.adapter)}
                disabled={editState.monthly_limit_credits === null}
              >
                <Trans>Remove limit</Trans>
              </Button>
              <Group gap="sm">
                <Button variant="default" onClick={onCloseEdit}>
                  <Trans>Cancel</Trans>
                </Button>
                <Button loading={saving} onClick={() => onSaveBudget(editState)}>
                  <Trans>Save</Trans>
                </Button>
              </Group>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  )
}
